// Packages
import { useEffect, useLayoutEffect } from "react"
import PropTypes from "prop-types"
import { useDispatch } from "react-redux"
import getValue from "object-getvalue"
import MediaQuery from "react-responsive"
import classNames from "classnames"
import { CSSTransition } from "react-transition-group"
import queryString from "query-string"

// Utils
import { toggleTheme } from "lib/utils/theme-util"
import {
  addtoDataLayer,
  removefromDataLayer,
} from "components/Stacker/utils/DataLayer"
import { setScroll } from "components/Stacker/utils/Scrolling"
import isMobile from "components/Stacker/utils/device-check"

//	Configs
import BreakPointConfig from "components/Stacker/constants/breakpoints"
import ToastConfig from "components/Stacker/config/ToastConfig"
import PRConfig from "components/Stacker/config/ProductConfiguratorConfig"
import ThemeConfig from "components/Stacker/config/ThemeConfig"

// Constants
import ESID from "components/Stacker/constants/EmailSendItemsConstants"
import TC from "components/Stacker/constants/TrackingConstants"

// Actions
import { getProductDetails } from "components/Stacker/actions/ProductDetailActions"
import { resetPRC } from "components/Stacker/actions/ProductConfiguratorActions"
import { toggleToggle } from "components/Stacker/actions/ToggleActions"

// Components
import ProductDetailsImages from "components/Stacker/components/ProductDetailsImages/ProductDetailsImages"
import ProductOverview from "components/Stacker/components/ProductOverview/ProductOverview"
import Loader from "components/Stacker/components/Loader/Loader"
import ProductDetails from "components/Stacker/components/ProductDetails/ProductDetails"
import PromoContent from "components/Stacker/components/PromoContent/PromoContent"
import ProductZoom from "components/Stacker/components/ProductZoom/ProductZoom"
import PageInfo from "components/Stacker/components/PageInfo/PageInfo"
import ProductConfiguratorPage from "components/Stacker/components/ProductConfiguratorPage/ProductConfiguratorPage"
import ProductName from "components/Stacker/components/ProductName/ProductName"
//import ViewDetailsLink from "components/Stacker/components/ViewDetailsLink/ViewDetailsLink"
import ProductStackItWith from "components/Stacker/components/ProductStackItWith/ProductStackItWith"
import AmuletStackerPromo from "components/Stacker/components/AmuletStackerPromo/AmuletStackerPromo"

// Styles
import "./ProductDetailsPage.scss"
import { getDY_SR } from "stackerConfig"

import { useContext } from "react"
import { StackerContext } from "components/DYStackers/StackerContext"
import { useSelector } from "react-redux"
import { useLocation } from "react-router"

function ProductDetailsPage({ isQuickView }) {
  const dispatch = useDispatch()
  const stackerContext = useContext(StackerContext)
  const location = useLocation()

  const {
    enableRopis,
    pid,
    //quickViewPDPUrl,
    baseStore,
    swatch,
    cartMetaData,
    enagraveableProductConfig,
    zoomToastState,
    pdp,
    isProductConfigurable,
    productTheme,
    isEPS,
    isPriceDisclaimerEnabled,
    isAffirmEnabledOnSite,
    isCsAgent,
  } = useSelector((state) => {
    let pid = state.pdp.currentQuickViewSelection.pid || getDY_SR().data.pid

    pid = pid ? pid.toUpperCase() : pid
    const product_details = state.pdp[pid] // getValue(state, `pdp.${pid}`, {})

    return {
      enableRopis: state.pdp.enableRopis,
      pid,
      quickViewPDPUrl: state.pdp.currentQuickViewSelection.url,
      pdp: product_details,
      isProductConfigurable:
        product_details?.data?.products.isProductConfigurable ?? false,
      productTheme: product_details?.productTheme ?? "women",
      swatch: state.swatch,
      baseStore: state.pdp.queryParams.baseStore,
      cartMetaData: state.cart.meta,
      enagraveableProductConfig: state.toggle.enagraveableProductConfig,
      zoomToastState: state.toast[ToastConfig.PDP_TOAST.NAME],
      isEPS: state.application.isEPS,
      isPriceDisclaimerEnabled: state.application.isPriceDisclaimerEnabled,
      isAffirmEnabledOnSite: state.application.isAffirmEnabledOnSite,
      isCsAgent: false,
    }
  })

  useEffect(() => {
    if (!pid) {
      window.location.reload(true)
    }
    document.documentElement.setAttribute(
      "lang",
      getValue(window, "recaptchaOptions.lang", "")
    )
    return () => {
      if (!isQuickView) {
        removefromDataLayer(TC.LAYER_PRODUCT_VIEW)
        if (isProductConfigurable) {
          dispatch(resetPRC())
          if (enagraveableProductConfig) {
            dispatch(toggleToggle("enagraveableProductConfig"), false)
          }
        }
      }
    }
  }, [
    dispatch,
    enagraveableProductConfig,
    isProductConfigurable,
    isQuickView,
    pid,
  ])

  useLayoutEffect(() => {
    const queryObj = queryString.parse(location.search, { decode: true })

    if (pid) {
      if (!pdp?.data) {
        let selectedSwatch = Object.keys(swatch).length > 0 ? swatch[pid] : {}

        // if there is a SwatchCode present in the PDP URL , then take that as selected Swatch Code.
        const { swatchCode } = queryObj
        if (swatchCode) {
          selectedSwatch = {
            baseProductCode: pid,
            selectedVariantCode: swatchCode,
            selectedVariantIndex: 0,
          }
        }

        dispatch(
          getProductDetails({
            args: {
              productId: pid,
              baseStore,
              selectedSwatch,
              isQuickView,
            },
            retailerId: stackerContext.retailerId,
          })
        )
      }

      if (pdp?.data) {
        addtoDataLayer(TC.LAYER_PAGE, pdp?.data.pageInfo)
      }
    }
  }, [pid, pdp])

  useEffect(() => {
    // this is needed in the case where you navigate to PDP from Quickview. As the location changes first and then the component Unmounts later
    // and the same component mounts again , thus the Scroll doesnt take precedence. Hence we need to fire the Scroll again.

    if (!isQuickView) {
      setScroll()
      // If already present [in case of back button, data is already present in reducer]
      if (productTheme) {
        toggleTheme(productTheme)
      }
    }
  }, [isQuickView, productTheme])

  if (!pdp?.data) {
    return <Loader />
  }

  const {
    data: {
      products,
      products: {
        configurableProductType,
        cropQuadrant,
        collection,
        categoryPath,
        isEngagementProduct,
        gender = "",
      },
      sizeChart,
      metals,
      pageInfo,
    },
    selectedVariant,
    selectedVariant: {
      productName,
      isAffirmEnabled,
      contactSpecialist = "false",
    },
    selectedVariantIndex,
    priceInventory,
    priceInventory: {
      //price: { formattedValue, discountedPrice, value } = {},
      priceList,
    } = {},
    selectedSize,
    promosToRender,
    sizeSelectionError,
    isGiftCard,
    selectedAmount,
    selectedVariantTimeStamp,
    canonicalUrl,
  } = pdp

  let { price: { formattedValue, discountedPrice, value } = {} } =
    pdp?.priceInventory ?? {}

  if (!value) {
    value = parseFloat(pdp.data.products.price.value)
    formattedValue = pdp.data.products.price.formattedValue
    discountedPrice = parseFloat(pdp.data.products.price.value)
  }

  let productImages = isGiftCard
    ? [selectedVariant.imageURL]
    : selectedVariant.imageVariants

  if (selectedSize && Object.keys(selectedSize).length) {
    const { imageVariants: selectedSizeImageVariants } = selectedSize
    productImages =
      selectedSizeImageVariants && selectedSizeImageVariants.length > 0
        ? selectedSizeImageVariants
        : productImages
  }

  const displayAffirm = isAffirmEnabledOnSite && isAffirmEnabled
  const priceData =
    priceList !== undefined
      ? priceList["de.hybris.platform.commercefacades.product.data.PriceData"]
      : []

  let configProductSet
  if (isProductConfigurable) {
    configProductSet =
      productTheme === ThemeConfig.WOMEN
        ? PRConfig[configurableProductType].WOMEN
        : PRConfig[configurableProductType].MEN
  } else {
    configProductSet = []
  }
  let defaultProductPrice = ""
  if (!priceInventory && contactSpecialist === "true") {
    defaultProductPrice =
      selectedVariant &&
      selectedVariant.price &&
      selectedVariant.price.formattedValue
        ? selectedVariant.price.formattedValue
        : ""
  }

  const productMeaning =
    selectedVariant && selectedVariant.meaningDescription
      ? selectedVariant.meaningDescription
      : ""

  const isAmulet = products.productId.substring(0, 1) === "D"
  const isBracelet = products.productId.substring(0, 1) === "B"
  const isRing =
    products.productId.substring(0, 1) === "R" ||
    products.productId.substring(0, 1) === "W"
  const isZoomEnabled = selectedVariant?.isZoomEnabled ? true : false

  return (
    <article
      className={classNames("ProductDetailsPage", {
        [productTheme]: isQuickView,
        isProductConfigurable,
      })}
    >
      <PageInfo
        title={products.productName}
        description={products.productName}
        canonicalUrl={canonicalUrl}
      />
      {isProductConfigurable && (
        <CSSTransition timeout={{ enter: 400, exit: 400 }} classNames="fadeIn">
          {enagraveableProductConfig && (
            <ProductConfiguratorPage
              isQuickView={isQuickView}
              swatch={swatch}
              productTheme={productTheme}
              configProductSet={configProductSet}
              configurableProductType={configurableProductType}
            />
          )}
        </CSSTransition>
      )}
      {!enagraveableProductConfig && (
        <div
          className={classNames("product-image-wrap d-flex flex-wrap", {
            "pdp-page-wrapper": !isQuickView,
          })}
        >
          <div className="modal-product-image-container">
            <MediaQuery query={BreakPointConfig.IS_MOBILE}>
              <ProductDetailsImages
                isProductConfigurable={isProductConfigurable}
                productImages={productImages}
                showThumbs={false}
                isZoomEnabled={isZoomEnabled}
                isGiftCard={isGiftCard}
                productId={products.productId}
                productName={productName}
                cropQuadrant={cropQuadrant}
                isMobile
                productTheme={productTheme}
                configProductSet={configProductSet}
                isQuickView={isQuickView && !isMobile()}
              />
            </MediaQuery>
            <MediaQuery query={BreakPointConfig.IS_DESKTOP}>
              <ProductDetailsImages
                isProductConfigurable={isProductConfigurable}
                productImages={productImages}
                showThumbs
                isZoomEnabled={isZoomEnabled}
                isQuickView={isQuickView && !isMobile()}
                isGiftCard={isGiftCard}
                zoomToastState={zoomToastState}
                timeStamp={selectedVariantTimeStamp}
                isMobile={isMobile()}
                productId={products.productId}
                productName={productName}
                cropQuadrant={cropQuadrant}
                image360DegreeUrl={selectedVariant.image360DegreeUrl}
                image360DegreeCount={selectedVariant.image360DegreeCount}
                productTheme={productTheme}
                configProductSet={configProductSet}
              />
            </MediaQuery>
            <ProductZoom
              productImages={productImages} // use this to simulate only one Image in array selectedVariant.imageVariants.slice(0, 1)
              cropQuadrant={cropQuadrant}
            />
          </div>
          <div className="d-xl-flex flex-column justify-content-center product-content-container">
            <ProductName
              collection={collection}
              categoryPath={categoryPath}
              productName={productName}
              isEngagementProduct={isEngagementProduct}
              isGiftCard={isGiftCard}
              formattedValue={formattedValue}
              priceList={priceData}
              discountedPrice={discountedPrice}
              isPriceDisclaimerEnabled={isPriceDisclaimerEnabled}
              displayAffirm={displayAffirm}
              price={value}
              productTheme={productTheme}
              isCsAgent={isCsAgent}
              gender={gender}
              defaultProductPrice={defaultProductPrice}
            />
            {isQuickView && <p className="product-meaning">{productMeaning}</p>}
            <ProductOverview
              enableRopis={enableRopis}
              productDetails={products}
              pid={pid}
              selectedVariant={selectedVariant}
              selectedVariantIndex={selectedVariantIndex}
              priceInventory={priceInventory}
              priceList={priceData}
              selectedSize={selectedSize}
              baseStore={baseStore}
              productSizeChart={sizeChart}
              isQuickView={isQuickView}
              productMetals={metals}
              sizeSelectionError={sizeSelectionError}
              isGiftCard={isGiftCard}
              selectedAmount={selectedAmount}
              cartMetaData={cartMetaData}
              displayAddtoBag={!isProductConfigurable}
              isEPS={isEPS}
              isPDPPage
              isStackerPage={false}
              isCsAgent={isCsAgent}
              contactSpecialist={contactSpecialist}
            />

            {/*{isQuickView && <ViewDetailsLink url={quickViewPDPUrl} pid={pid} />}*/}
          </div>
        </div>
      )}
      {!isQuickView && (
        <ProductDetails
          products={products}
          selectedVariant={selectedVariant}
          selectedSize={selectedSize}
          isGiftCard={isGiftCard}
          sendType={ESID.SEND_PDP}
        />
      )}
      {isAmulet && !isQuickView && (
        <AmuletStackerPromo
          selectedVariant={selectedVariant}
          productId={products.productId}
        />
      )}
      {(isBracelet || isRing) && !isQuickView && (
        <ProductStackItWith
          selectedVariant={selectedVariant}
          // selectedVariant={{ code: 'B12567DSSADI' }}
          // selectedVariant={{ code: 'R07437 S8ZZZ' }}
        />
      )}
      {!isQuickView &&
        promosToRender &&
        promosToRender.length > 0 &&
        promosToRender.map((path, index) => {
          return <PromoContent key={`${path}-${index}`} promoHTML={path} />
        })}
    </article>
  )
}

ProductDetailsPage.propTypes = {
  isQuickView: PropTypes.bool,
}

export default ProductDetailsPage
